/**
 * Hook that encapsulates section-managed content reference section behavior:
 * initial state from GraphQL, local state, dirty sync, mutations, save handler ref, reset.
 *
 * Section-managed pattern: Section owns its mutations (CREATE_CONTENT_REFERENCES, DELETE_CONTENT_REFERENCE)
 * and exposes save via ref. Parent just calls it when user clicks Save.
 *
 * Usage example:
 *   function MyReferencesSection({ contentData, isViewOnly, onDirtyChange, saveHandlerRef, onResetTriggered }) {
 *     const { contentReferences, handleContentReferencesChange } = useContentReferencesSection({
 *       refTypes: MY_REFERENCE_TYPES,
 *       contentData,
 *       onDirtyChange,
 *       saveHandlerRef,
 *       onResetTriggered,
 *     })
 *
 *     return MY_REFERENCE_TYPES.map(refType => (
 *       <ContentReferenceField
 *         key={refType}
 *         referenceType={refType}
 *         value={contentReferences[refType] || []}
 *         onChange={newRefs => handleContentReferencesChange(refType, newRefs)}
 *         disabled={isViewOnly}
 *       />
 *     ))
 *   }
 *
 * Real examples:
 * - VideoContentReferencesSection
 * - VideoContentReferencesExtendedSection
 * - VideoContentReferencesYoreSection
 *
 * For the full section-managed pattern guide, see useSectionManagedSlot.ts
 *
 * @module content-detail/useContentReferencesSection
 */

import { useState, useCallback, useMemo, useEffect } from 'react'
import { useMutation } from '@apollo/client'
import {
  buildInitialContentReferenceMap,
  isContentReferencesDirty,
  computeContentReferenceSavePayload,
} from './content-references/utils'
import { CREATE_CONTENT_REFERENCES, DELETE_CONTENT_REFERENCE } from './content-references/queries'
import type {
  ContentReference,
  ContentReferenceMap,
  ContentReferencesSectionContentData,
} from './types'

export interface UseContentReferencesSectionParams {
  /** Reference types this section manages (e.g. VIDEO_CONTENT_REFERENCE_TYPES). */
  refTypes: string[]
  /** Content with contentReferencesFrom from GraphQL. */
  contentData: ContentReferencesSectionContentData | null
  /** Called when dirty state changes (for parent floating save/cancel). */
  onDirtyChange: (isDirty: boolean) => void
  /** Ref to assign save handler so parent can call it on Save. */
  saveHandlerRef: React.MutableRefObject<(() => Promise<void>) | null>
  /** When this increments, section resets local state to initial. */
  onResetTriggered?: number
}

export interface UseContentReferencesSectionResult {
  contentReferences: ContentReferenceMap
  handleContentReferencesChange: (referenceType: string, newReferences: ContentReference[]) => void
  bulkSetContentReferences: (updates: ContentReferenceMap) => void
}

export function useContentReferencesSection ({
  refTypes,
  contentData,
  onDirtyChange,
  saveHandlerRef,
  onResetTriggered,
}: UseContentReferencesSectionParams): UseContentReferencesSectionResult {
  const initialContentReferences = useMemo(
    () => buildInitialContentReferenceMap(contentData?.contentReferencesFrom ?? null, refTypes),
    [contentData?.contentReferencesFrom, refTypes],
  )

  const [contentReferences, setContentReferences] =
    useState<ContentReferenceMap>(initialContentReferences)

  useEffect(() => {
    setContentReferences(initialContentReferences)
  }, [initialContentReferences])

  const isDirty = useMemo(
    () => isContentReferencesDirty(refTypes, initialContentReferences, contentReferences),
    [refTypes, initialContentReferences, contentReferences],
  )

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  const handleContentReferencesChange = useCallback(
    (referenceType: string, newReferences: ContentReference[]) =>
      setContentReferences((prev) => ({
        ...prev,
        [referenceType]: newReferences,
      })),
    [],
  )

  const bulkSetContentReferences = useCallback((updates: ContentReferenceMap) => {
    setContentReferences((prev) => ({ ...prev, ...updates }))
  }, [])

  const [createContentReferences] = useMutation(CREATE_CONTENT_REFERENCES)
  const [deleteContentReference] = useMutation(DELETE_CONTENT_REFERENCE)

  const handleSave = useCallback(async () => {
    const { toDelete, toCreate } = computeContentReferenceSavePayload(
      refTypes,
      contentReferences,
      initialContentReferences,
      contentData,
    )

    if (toDelete.length === 0 && toCreate.length === 0) {
      return
    }

    if (toDelete.length > 0) {
      await Promise.all(
        toDelete.map((item) =>
          deleteContentReference({
            variables: { contentReference: { id: item.referenceId } },
          }),
        ),
      )
    }

    if (toCreate.length > 0 && contentData?.contentId != null && contentData?.contentType != null) {
      await createContentReferences({
        variables: {
          contentReferences: toCreate,
          deleteOmittedReferences: false,
        },
      })
    }
  }, [
    refTypes,
    contentReferences,
    initialContentReferences,
    contentData,
    createContentReferences,
    deleteContentReference,
  ])

  useEffect(() => {
    const ref = saveHandlerRef
    ref.current = handleSave
    return () => {
      ref.current = null
    }
  }, [saveHandlerRef, handleSave])

  useEffect(() => {
    if (onResetTriggered !== undefined && onResetTriggered > 0) {
      setContentReferences(initialContentReferences)
    }
  }, [onResetTriggered, initialContentReferences])

  return {
    contentReferences,
    handleContentReferencesChange,
    bulkSetContentReferences,
  }
}
